import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';

/**
 * Returns raised against a seller.
 *
 * The mirror of the buyer's Returns tab. A seller needs different things from
 * this screen than a buyer does: what is being claimed, what evidence came with
 * it, how long they have to respond, and a way to simply agree when the buyer
 * is right. Previously a seller's only signal was a notification they might
 * have missed - and silence is read as conceding the case.
 */
interface SellerReturn {
  id: string;
  orderId: string;
  reference: string;
  productTitle: string;
  productImage?: string;
  amountGBP: number;
  reason: string;
  notes?: string;
  evidenceUrls: string[];
  state: 'OPEN' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED' | 'COMPLETE';
  responded: boolean;
  sellerResponse?: string;
  hoursLeftToRespond: number | null;
  expired: boolean;
  buyerHandle: string;
  buyerClaimCount: number;
  createdAt: string;
  resolvedAt?: string;
  resolution?: string;
}

const REASON_LABELS: Record<string, string> = {
  WRONG_ITEM: 'Wrong item sent',
  EMPTY_PARCEL: 'Empty / item missing',
  NOT_AS_DESCRIBED: 'Not as described',
  NOT_RECEIVED: 'Never arrived',
  DAMAGED: 'Arrived damaged',
  COUNTERFEIT: 'Suspected counterfeit',
};

const STATE_STYLE: Record<string, { label: string; cls: string }> = {
  OPEN:      { label: 'Needs your response', cls: 'bg-bad/10 text-bad' },
  IN_REVIEW: { label: 'Souqli reviewing',    cls: 'bg-warn/15 text-warn-dark' },
  APPROVED:  { label: 'Refunded to buyer',   cls: 'bg-warmline text-mute' },
  REJECTED:  { label: 'Decided in your favour', cls: 'bg-ok/15 text-ok-dark' },
  COMPLETE:  { label: 'Closed',              cls: 'bg-warmline text-mute' },
};

export default function SellerReturns() {
  const qc = useQueryClient();
  const [respondTo, setRespondTo] = useState<SellerReturn | null>(null);
  const [response, setResponse] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [acceptTo, setAcceptTo] = useState<SellerReturn | null>(null);
  const [acceptNote, setAcceptNote] = useState('');

  const { data: rows = [], isLoading } = useQuery<SellerReturn[]>({
    queryKey: ['seller-returns'],
    queryFn: async () => (await api.get('/me/seller-returns')).data,
  });

  const refresh = () => {
    ['seller-returns', 'sales', 'wallet'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const submitResponse = useMutation({
    mutationFn: async ({ orderId, fd }: { orderId: string; fd: FormData }) =>
      (await api.post(`/orders/${orderId}/dispute-response`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data,
    onSuccess: () => {
      toast.success('Response sent. Souqli will review both sides.');
      setRespondTo(null); setResponse(''); setPhoto(null);
      refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not send your response.'),
  });

  const accept = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (await api.post(`/returns/${id}/accept`, { note })).data,
    onSuccess: (r: any) => {
      toast.success(r?.message ?? 'Return accepted and refunded.');
      setAcceptTo(null); setAcceptNote('');
      refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not accept that return.'),
  });

  const open = rows.filter((r) => r.state === 'OPEN' || r.state === 'IN_REVIEW');
  const closed = rows.filter((r) => !['OPEN', 'IN_REVIEW'].includes(r.state));
  const atRisk = open.reduce((sum, r) => sum + r.amountGBP, 0);

  return (
    <div>
      <div className="stamp text-gold-dark text-[10px]">RETURNS &amp; DISPUTES</div>
      <h1 className="display text-4xl mt-2">Returns</h1>
      <p className="text-mute text-[14px] mt-2 max-w-[62ch]">
        When a buyer opens a return, their payment stays held by Souqli until it’s resolved.
        You can accept it and refund straight away, or respond with evidence and let Souqli decide.
      </p>

      {!!open.length && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
          <div className="card-flat p-4">
            <div className="stamp text-[9px] text-mute">NEEDS ACTION</div>
            <div className="display text-2xl mt-1">{open.filter((r) => !r.responded).length}</div>
          </div>
          <div className="card-flat p-4">
            <div className="stamp text-[9px] text-mute">HELD PENDING</div>
            <div className="display text-2xl mt-1">£{atRisk.toFixed(2)}</div>
          </div>
          <div className="card-flat p-4">
            <div className="stamp text-[9px] text-mute">RESOLVED</div>
            <div className="display text-2xl mt-1">{closed.length}</div>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-mute text-sm mt-6">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="card-flat p-10 text-center mt-6">
          <div className="text-3xl mb-3">✓</div>
          <h2 className="display text-xl mb-2">No returns</h2>
          <p className="text-mute text-[14px] max-w-[44ch] mx-auto">
            Nobody has opened a return against you. Accurate photos and honest condition notes are
            what keep it that way.
          </p>
        </div>
      ) : (
        <div className="space-y-4 mt-6">
          {[...open, ...closed].map((r) => {
            const badge = STATE_STYLE[r.state] ?? STATE_STYLE.COMPLETE;
            const urgent = r.state === 'OPEN' && !r.responded;
            return (
              <div key={r.id} className={`card p-5 ${urgent ? 'border-bad/30' : ''}`}>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-luxe bg-warmline overflow-hidden shrink-0">
                    {r.productImage && <img src={r.productImage} alt="" className="w-full h-full object-cover" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`pill text-[9px] ${badge.cls}`}>{badge.label.toUpperCase()}</span>
                      <span className="stamp text-[9px] text-mute mono">{r.reference}</span>
                      {/* A buyer who claims constantly is the main fraud pattern. */}
                      {r.buyerClaimCount >= 3 && (
                        <span className="pill text-[9px] bg-warn/15 text-warn-dark">
                          BUYER HAS {r.buyerClaimCount} CLAIMS
                        </span>
                      )}
                    </div>

                    <h3 className="text-[15px] font-medium mt-1.5 truncate">{r.productTitle}</h3>
                    <p className="text-[13px] text-mute mt-0.5">
                      £{r.amountGBP.toFixed(2)} · {r.buyerHandle} · {REASON_LABELS[r.reason] ?? r.reason}
                    </p>

                    {r.notes && (
                      <blockquote className="mt-3 border-l-2 border-warmline pl-3 text-[13px] text-ink-700 leading-relaxed">
                        “{r.notes}”
                      </blockquote>
                    )}

                    {!!r.evidenceUrls.length && (
                      <div className="flex gap-2 mt-3">
                        {r.evidenceUrls.map((u, i) => (
                          <a key={i} href={u} target="_blank" rel="noreferrer"
                             className="w-14 h-14 rounded-luxe overflow-hidden border border-warmline block">
                            <img src={u} alt="Buyer evidence" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}

                    {r.responded && r.sellerResponse && (
                      <div className="mt-3 rounded-luxe bg-cream-deep/50 p-3">
                        <div className="stamp text-[9px] text-mute">YOUR RESPONSE</div>
                        <p className="text-[13px] mt-1 leading-relaxed">{r.sellerResponse}</p>
                      </div>
                    )}

                    {r.resolution && (
                      <div className="mt-3 rounded-luxe bg-ok/5 border border-ok/20 p-3">
                        <div className="stamp text-[9px] text-ok-dark">OUTCOME</div>
                        <p className="text-[13px] mt-1 leading-relaxed">{r.resolution}</p>
                      </div>
                    )}

                    {/* Deadline pressure, stated plainly rather than hidden. */}
                    {urgent && (
                      <p className={`text-[12px] mt-3 ${r.expired ? 'text-bad' : 'text-warn-dark'}`}>
                        {r.expired
                          ? 'The response window has closed. Souqli will decide on the evidence available.'
                          : `${r.hoursLeftToRespond}h left to respond. No response is treated as accepting the claim.`}
                      </p>
                    )}

                    {(r.state === 'OPEN' || r.state === 'IN_REVIEW') && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {!r.responded && (
                          <button onClick={() => setRespondTo(r)} className="btn btn-primary btn-sm">
                            Respond with evidence
                          </button>
                        )}
                        <button onClick={() => setAcceptTo(r)} className="btn btn-outline btn-sm">
                          Accept &amp; refund £{r.amountGBP.toFixed(2)}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Respond with evidence */}
      {respondTo && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setRespondTo(null)}>
          <div className="bg-white rounded-2xl w-full max-w-[460px] overflow-hidden overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-warmline">
              <div className="stamp text-gold-dark text-[9px]">DEFEND THIS SALE</div>
              <h3 className="display text-2xl mt-1">Your side of it</h3>
              <p className="text-[12px] text-mute mt-1 truncate">{respondTo.productTitle}</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="stamp text-[10px] text-mute block mb-1.5">WHAT HAPPENED</label>
                <textarea
                  className="input w-full h-28 resize-none"
                  maxLength={2000}
                  autoFocus
                  placeholder="Describe the item's condition when you sent it, what was included, and anything that contradicts the claim."
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                />
              </div>

              <div>
                <label className="stamp text-[10px] text-mute block mb-1.5">PHOTO EVIDENCE (STRONGLY ADVISED)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
                  className="text-[13px]"
                />
                <p className="text-[12px] text-mute mt-1.5 leading-relaxed">
                  A dated photo of the item packed, or of the label, decides most cases. Text alone rarely does.
                </p>
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-2">
              <button onClick={() => setRespondTo(null)} className="btn btn-outline flex-1">Cancel</button>
              <button
                disabled={response.trim().length < 2 || submitResponse.isPending}
                onClick={() => {
                  const fd = new FormData();
                  fd.append('response', response.trim());
                  if (photo) fd.append('photo', photo);
                  submitResponse.mutate({ orderId: respondTo.orderId, fd });
                }}
                className="btn btn-primary flex-1"
              >
                {submitResponse.isPending ? 'Sending…' : 'Send response'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept and refund */}
      {acceptTo && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setAcceptTo(null)}>
          <div className="bg-white rounded-2xl w-full max-w-[420px] overflow-hidden overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-warmline">
              <div className="stamp text-gold-dark text-[9px]">ACCEPT THE RETURN</div>
              <h3 className="display text-2xl mt-1">Refund £{acceptTo.amountGBP.toFixed(2)}?</h3>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-[14px] text-mute leading-relaxed">
                The buyer is refunded in full and the case closes immediately. The money was still held by
                Souqli, so nothing is taken from your wallet.
              </p>
              <p className="text-[13px] leading-relaxed rounded-luxe bg-ok/5 border border-ok/20 p-3">
                Accepting doesn’t count against you. Sellers who resolve returns quickly keep a better rating
                than those who contest and lose.
              </p>

              <div>
                <label className="stamp text-[10px] text-mute block mb-1.5">NOTE TO THE BUYER (OPTIONAL)</label>
                <input
                  className="input w-full"
                  maxLength={500}
                  placeholder="Sorry about that - refunded in full."
                  value={acceptNote}
                  onChange={(e) => setAcceptNote(e.target.value)}
                />
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-2">
              <button onClick={() => setAcceptTo(null)} className="btn btn-outline flex-1">Not yet</button>
              <button
                disabled={accept.isPending}
                onClick={() => accept.mutate({ id: acceptTo.id, note: acceptNote.trim() || undefined })}
                className="btn btn-primary flex-1"
              >
                {accept.isPending ? 'Refunding…' : 'Accept & refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
